// @ts-nocheck

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ColorBlindType, ColorBlindContextType } from './types';

const ColorBlindContext = createContext<ColorBlindContextType | undefined>(undefined);

/**
 * Hook pour utiliser le filtre de daltonisme dans n'importe quel composant
 * @throws {Error} Si utilisé en dehors du ColorBlindFilterProvider
 * @returns {ColorBlindContextType} L'état du filtre et la fonction pour le modifier
 */
export const useColorBlindFilter = (): ColorBlindContextType => {
  const context = useContext(ColorBlindContext);
  if (!context) {
    throw new Error('useColorBlindFilter doit être utilisé dans ColorBlindFilterProvider');
  }
  return context;
};

// Configuration des matrices de couleurs pour chaque type de daltonisme
const filterConfigs = {
  protanopia: {
    name: 'Protanopie (Rouge)',
    matrix: '0.567, 0.433, 0, 0, 0, 0.558, 0.442, 0, 0, 0, 0, 0.242, 0.758, 0, 0, 0, 0, 0, 1, 0'
  },
  deuteranopia: {
    name: 'Deutéranopie (Vert)',
    matrix: '0.625, 0.375, 0, 0, 0, 0.7, 0.3, 0, 0, 0, 0, 0.3, 0.7, 0, 0, 0, 0, 0, 1, 0'
  },
  tritanopia: {
    name: 'Tritanopie (Bleu)',
    matrix: '0.95, 0.05, 0, 0, 0, 0, 0.433, 0.567, 0, 0, 0, 0.475, 0.525, 0, 0, 0, 0, 0, 1, 0'
  },
  achromatopsia: {
    name: 'Achromatopsie (Aucune couleur)',
    matrix: '0.299, 0.587, 0.114, 0, 0, 0.299, 0.587, 0.114, 0, 0, 0.299, 0.587, 0.114, 0, 0, 0, 0, 0, 1, 0'
  }
};

interface ColorBlindFilterProviderProps {
  children: ReactNode;
}

/**
 * Provider pour le filtre de daltonisme
 * Doit envelopper votre application à la racine
 */
export const ColorBlindFilterProvider: React.FC<ColorBlindFilterProviderProps> = ({ children }) => {
  const [filterType, setFilterType] = useState<ColorBlindType>('none');

  useEffect(() => {
    const root = document.documentElement;
    
    if (filterType !== 'none') {
      root.style.filter = `url(#colorblind-filter-${filterType})`;
    } else {
      root.style.filter = 'none';
    }

    // Cleanup: retire le filtre quand le composant est démonté
    return () => {
      root.style.filter = 'none';
    };
  }, [filterType]);

  return (
    <ColorBlindContext.Provider value={{ filterType, setFilterType }}>
      {/* Filtres SVG invisibles */}
      <svg style={{ position: 'absolute', width: 0, height: 0, pointerEvents: 'none' }} aria-hidden="true">
        <defs>
          {Object.entries(filterConfigs).map(([key, config]) => (
            <filter key={key} id={`colorblind-filter-${key}`}>
              <feColorMatrix type="matrix" values={config.matrix} />
            </filter>
          ))}
        </defs>
      </svg>
      
      {children}
    </ColorBlindContext.Provider>
  );
};

interface ColorBlindFilterSelectProps {
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Composant Select prêt à l'emploi pour changer le filtre
 * @param {string} className - Classes CSS personnalisées
 * @param {React.CSSProperties} style - Styles inline personnalisés
 */
export const ColorBlindFilterSelect: React.FC<ColorBlindFilterSelectProps> = ({ 
  className = '', 
  style = {} 
}) => {
  const { filterType, setFilterType } = useColorBlindFilter();

  return (
    <select
      value={filterType}
      onChange={(e) => setFilterType(e.target.value as ColorBlindType)}
      className={className}
      aria-label="Sélectionner un filtre de daltonisme"
      style={{
        padding: '8px 12px',
        borderRadius: '6px',
        border: '1px solid #d1d5db',
        fontSize: '14px',
        cursor: 'pointer',
        backgroundColor: '#ffffff',
        ...style
      }}
    >
      <option value="none">Vision normale</option>
      <option value="protanopia">Protanopie (Rouge)</option>
      <option value="deuteranopia">Deutéranopie (Vert)</option>
      <option value="tritanopia">Tritanopie (Bleu)</option>
      <option value="achromatopsia">Achromatopsie (N&B)</option>
    </select>
  );
};